import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Sparkles, Clock, Users, Globe, Check, Phone, Mail, ExternalLink } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import { useLang } from '../i18n';
import { useCurrency } from '../currency';

export default function ExperienceDetail() {
  const { slug, expSlug } = useParams();
  const { t } = useLang();
  const { format: formatCurrency } = useCurrency();
  const { data: exp, loading, error } = useFetch(`experience:${expSlug}`, () => publicApi.experience(expSlug), [expSlug]);

  // Prices in DB always in EUR — convert via currency context
  const priceLabel = (exp) => {
    if (typeof exp?.price !== 'number') return null;
    return formatCurrency(exp.price, { decimals: 0 });
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;
  if (error || !exp) {
    return (
      <div className="max-w-4xl mx-auto py-24 px-6 text-center">
        <h1 className="text-3xl font-bold text-[color:var(--yup-primary)]">404</h1>
        <p className="mt-3 text-gray-400">Experience non trovata</p>
        <Link to={`/${slug}/experiences`} className="inline-flex items-center gap-2 mt-6 text-[color:var(--yup-primary)] font-semibold">
          <ArrowLeft size={16} /> Torna alle Experiences
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`experience-detail-${expSlug}`}>
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full min-h-[45vh] md:min-h-[55vh] flex items-end">
          {exp.image && (
            <img src={resolveImage(exp.image)} alt={exp.title} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 pb-12 pt-24 text-white w-full">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest text-black mb-4"
                  style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
              <Sparkles size={10}/> YUP Experience
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {exp.title}
            </h1>
            {exp.subtitle && <p className="mt-4 text-white/80 text-lg max-w-2xl">{exp.subtitle}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/80">
              {exp.duration && <span className="inline-flex items-center gap-1.5"><Clock size={14}/> {exp.duration}</span>}
              {exp.max_people && <span className="inline-flex items-center gap-1.5"><Users size={14}/> max {exp.max_people} pers.</span>}
              {exp.languages?.length > 0 && <span className="inline-flex items-center gap-1.5"><Globe size={14}/> {exp.languages.map(l => l.toUpperCase()).join(' / ')}</span>}
              {priceLabel(exp) && (
                <span className="ml-auto px-3 py-1 rounded-full text-sm font-bold text-black"
                      style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>
                  da {priceLabel(exp)}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-5xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="md:col-span-2 text-white/85 leading-relaxed space-y-6">
          {exp.description && (
            <article className="whitespace-pre-wrap text-base md:text-lg text-white/80">
              {exp.description}
            </article>
          )}
          {exp.highlights?.length > 0 && (
            <div>
              <h2 className="text-xs tracking-[0.3em] uppercase text-[color:var(--yup-primary)] font-semibold mb-3">Highlights</h2>
              <ul className="space-y-2">
                {exp.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80"><Check size={16} className="text-[color:var(--yup-primary)] mt-1 flex-shrink-0"/> <span>{h}</span></li>
                ))}
              </ul>
            </div>
          )}
          {exp.includes?.length > 0 && (
            <div>
              <h2 className="text-xs tracking-[0.3em] uppercase text-[color:var(--yup-primary)] font-semibold mb-3">Cosa include</h2>
              <ul className="space-y-2">
                {exp.includes.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80"><Check size={16} className="text-[color:var(--yup-primary)] mt-1 flex-shrink-0"/> <span>{h}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Booking box */}
        <aside className="md:sticky md:top-24 h-fit rounded-2xl border border-[color:var(--yup-primary)]/40 bg-black/60 backdrop-blur-md p-6">
          <h3 className="text-lg font-bold text-white mb-2">Prenota questa experience</h3>
          <p className="text-xs text-white/50 uppercase tracking-widest mb-4">Contatta lo staff YUP</p>
          <div className="space-y-2">
            {exp.booking_url && (
              <a href={exp.booking_url} target="_blank" rel="noopener noreferrer"
                 className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold text-black text-sm"
                 style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
                 data-testid="experience-book-btn">
                Prenota ora <ExternalLink size={14}/>
              </a>
            )}
            {exp.booking_phone && (
              <a href={`tel:${exp.booking_phone}`} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/20 text-white text-sm hover:bg-white/5">
                <Phone size={14}/> {exp.booking_phone}
              </a>
            )}
            {exp.booking_email && (
              <a href={`mailto:${exp.booking_email}`} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-white/20 text-white text-sm hover:bg-white/5">
                <Mail size={14}/> Scrivi allo staff
              </a>
            )}
            {!exp.booking_url && !exp.booking_phone && !exp.booking_email && (
              <p className="text-white/50 text-xs">Contattaci via chat per prenotare.</p>
            )}
          </div>
          <Link to={`/${slug}/experiences`} className="mt-6 inline-flex items-center gap-2 text-[color:var(--yup-primary)] text-xs font-semibold">
            <ArrowLeft size={14}/> Torna alle Experiences
          </Link>
        </aside>
      </section>
    </div>
  );
}
